import {
  ActionType,
  Connection,
  FlowVersion,
  Step,
  flowStructureUtil,
} from '@activepieces/shared';

type ConnectedFlow =
  | FlowVersion
  | { connections?: Connection[]; actions?: Step[] };

/** The flows nested in an action: a loop's body, a router's branches */
const childFlowsOf = (action: Step): ConnectedFlow[] => {
  if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
    return [action.subflow];
  }
  if (action.type === ActionType.ROUTER && Array.isArray(action.children)) {
    return action.children.filter((child) => child !== null);
  }
  return [];
};

/**
 * What the canvas draws, as one string: every step at every depth (loop
 * bodies, router branches), its name, kind, label and successor, plus every
 * connection. Two versions with the same key draw the same graph.
 */
export const canvasGraphKey = (flowVersion: FlowVersion): string => {
  const allSteps = flowStructureUtil.getAllSteps(flowVersion);
  const stepsKey = allSteps
    .map((step) => {
      const nextName = step.nextAction?.name || '';
      let extra = '';
      if (step.type === ActionType.ROUTER) {
        extra = Array.isArray(step.settings.branches)
          ? step.settings.branches.map((branch) => branch.branchName).join('-')
          : '';
      }
      return `${step.name}-${step.displayName}-${step.type}-${nextName}-${extra}`;
    })
    .join('|');

  function getConnectionsKey(flow: ConnectedFlow): string {
    let key = '';
    if (flow.connections && flow.connections.length > 0) {
      key += flow.connections
        .map((conn) => `${conn.sourceId}-${conn.targetId}`)
        .join('|');
    }
    for (const action of flow.actions ?? []) {
      for (const childFlow of childFlowsOf(action)) {
        key += '|' + getConnectionsKey(childFlow);
      }
    }
    return key;
  }

  const connectionsKey = getConnectionsKey(flowVersion);
  return `${stepsKey}-${connectionsKey}`;
};
